import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { testIds } from "@/testIds";

/**
 * The warning underline under characters that won't compile.
 *
 * Derived, never stored (Olivia, 25 Sep): the set of characters comes from the
 * server's check — of the whole document on load, and of each paste — and the
 * underline is recomputed from the text. So it survives the chip's dismissal
 * and survives reload without anything written into the document, and it
 * disappears the moment the character is gone.
 */

export const wontCompileKey = new PluginKey<WontCompileState>("wontCompile");

interface WontCompileState {
  chars: ReadonlySet<string>;
  decorations: DecorationSet;
}

export const WONT_COMPILE_CLASS = "lilia-wont-compile";
export const WONT_COMPILE_TITLE = "Won't compile — LaTeX has no command for this character here";

export function wontCompileDecorations(doc: PMNode, chars: ReadonlySet<string>): DecorationSet {
  if (chars.size === 0) return DecorationSet.empty;
  const found: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    let offset = 0;
    for (const ch of node.text) {
      if (chars.has(ch)) {
        found.push(Decoration.inline(pos + offset, pos + offset + ch.length, {
          class: WONT_COMPILE_CLASS,
          title: WONT_COMPILE_TITLE,
          "data-testid": testIds.pasteFix.underlined,
        }));
      }
      offset += ch.length;
    }
  });
  return DecorationSet.create(doc, found);
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    wontCompile: {
      /** Add characters the server says won't compile. Additive: a paste check
       *  never clears what the load check found. */
      markWontCompile: (chars: Iterable<string>) => ReturnType;
    };
  }
}

export const WontCompileUnderline = Extension.create({
  name: "wontCompileUnderline",

  addCommands() {
    return {
      markWontCompile:
        (chars) =>
        ({ tr, dispatch }) => {
          if (dispatch) tr.setMeta(wontCompileKey, [...chars]);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<WontCompileState>({
        key: wontCompileKey,
        state: {
          init: () => ({ chars: new Set(), decorations: DecorationSet.empty }),
          apply(tr, prev, _old, state) {
            const added = tr.getMeta(wontCompileKey) as string[] | undefined;
            if (added && added.some((c) => !prev.chars.has(c))) {
              const chars = new Set([...prev.chars, ...added]);
              return { chars, decorations: wontCompileDecorations(state.doc, chars) };
            }
            if (!tr.docChanged || prev.chars.size === 0) return prev;
            return { chars: prev.chars, decorations: wontCompileDecorations(state.doc, prev.chars) };
          },
        },
        props: {
          decorations: (state) => wontCompileKey.getState(state)?.decorations,
        },
      }),
    ];
  },
});
